/**
 * GlobalScannerModal
 *
 * A universal PLX-tag scanner accessible from the top bar in every module.
 *
 * Flow:
 *   1. User clicks the scanner icon in the nav bar → modal opens
 *   2. Camera activates and scans for a PLX tag (reuses PLXScanner logic)
 *   3. On successful scan → calls GET /api/plx/lookup?code=<id>
 *   4. Result panel shows the matched record (asset / shipment / repair)
 *      with context-aware quick-action buttons
 *   5. Actions either navigate to a page or call an API directly
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, ScanLine, Package, Truck, Wrench, AlertCircle,
  ArrowRight, Tag, MapPin, ChevronRight, Loader, CheckCircle2,
} from 'lucide-react';
import PLXScanner from './PLXScanner';
import api from '../lib/api';
import { useTheme } from '../context/ThemeContext';

/* ── helpers ─────────────────────────────────────────────────────────────── */

const TYPE_META = {
  shipment: {
    icon: Truck,
    label: 'Shipment',
    color: 'text-violet-400',
    bg: 'bg-violet-500/10',
    border: 'border-violet-500/30',
    accent: '#7c3aed',
  },
  asset: {
    icon: Package,
    label: 'Asset',
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/30',
    accent: '#2874ba',
  },
  repair: {
    icon: Wrench,
    label: 'Repair Ticket',
    color: 'text-orange-400',
    bg: 'bg-orange-500/10',
    border: 'border-orange-500/30',
    accent: '#f7913e',
  },
};

const STATUS_COLORS = {
  // Delivery
  Imported:       'bg-gray-500/20 text-gray-300',
  'Labels Printed': 'bg-blue-500/20 text-blue-300',
  'In Transit':   'bg-amber-500/20 text-amber-300',
  Delivered:      'bg-emerald-500/20 text-emerald-300',
  // Repair
  'Sent to Vendor': 'bg-purple-500/20 text-purple-300',
  'Needs Approval': 'bg-amber-500/20 text-amber-300',
  'In Repair':    'bg-blue-500/20 text-blue-300',
  Complete:       'bg-emerald-500/20 text-emerald-300',
  // Asset
  Active:         'bg-emerald-500/20 text-emerald-300',
  Inactive:       'bg-gray-500/20 text-gray-300',
  'In Repair':    'bg-orange-500/20 text-orange-300',
};

function StatusBadge({ status }) {
  const cls = STATUS_COLORS[status] || 'bg-gray-500/20 text-gray-300';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {status}
    </span>
  );
}

/* ── Action button ───────────────────────────────────────────────────────── */
function ActionButton({ action, onAction, loading, done }) {
  const base = 'w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all';

  const variants = {
    primary:   'bg-violet-600 hover:bg-violet-500 text-white',
    secondary: 'bg-slate-700 hover:bg-slate-600 text-slate-100',
    ghost:     'bg-transparent border border-slate-700 hover:bg-slate-800 text-slate-300',
  };

  const cls = `${base} ${variants[action.variant] || variants.secondary} ${loading || done ? 'opacity-60 cursor-not-allowed' : ''}`;

  return (
    <button className={cls} onClick={() => onAction(action)} disabled={loading || done}>
      <span className="flex-1 text-left">{action.label}</span>
      {loading ? (
        <Loader size={14} className="animate-spin shrink-0" />
      ) : done ? (
        <CheckCircle2 size={14} className="text-emerald-400 shrink-0" />
      ) : (
        <ChevronRight size={14} className="shrink-0 opacity-60" />
      )}
    </button>
  );
}

/* ── Result panel ────────────────────────────────────────────────────────── */
function ResultPanel({ result, isDark, onAction, actionLoading, actionDone }) {
  if (!result) return null;

  if (result.type === null) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center gap-3">
        <AlertCircle size={40} className="text-amber-400" />
        <p className="text-sm font-medium text-slate-200">No record found</p>
        <p className="text-xs text-slate-500">
          This PLX tag isn't linked to any asset, shipment, or repair in your organisation.
        </p>
      </div>
    );
  }

  const meta = TYPE_META[result.type] || TYPE_META.asset;
  const Icon = meta.icon;
  const d = result.data;

  return (
    <div className="space-y-4">
      {/* Record header */}
      <div className={`rounded-xl border p-4 ${meta.bg} ${meta.border}`}>
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${meta.bg}`}>
            <Icon size={20} className={meta.color} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs font-semibold uppercase tracking-wider ${meta.color}`}>
                {meta.label}
              </span>
              <StatusBadge status={d.status || d.repair?.status} />
            </div>

            {/* Shipment */}
            {result.type === 'shipment' && (
              <>
                <p className="text-sm font-bold text-white mt-1 truncate">
                  {d.deliveryNo || d.shipmentNo || 'Shipment'}
                </p>
                {d.customerName && (
                  <p className="text-xs text-slate-400 mt-0.5 truncate">{d.customerName}</p>
                )}
                <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
                  {d.labelType && (
                    <div className="flex items-center gap-1 text-xs text-slate-400">
                      <Tag size={10} />
                      <span className="capitalize">{d.labelType} label {d.labelIndex}/{d.labelTotal}</span>
                    </div>
                  )}
                  {d.carrierName && (
                    <div className="flex items-center gap-1 text-xs text-slate-400">
                      <Truck size={10} />
                      <span className="truncate">{d.carrierName}</span>
                    </div>
                  )}
                  {d.customerAddress && (
                    <div className="flex items-center gap-1 text-xs text-slate-400 col-span-2">
                      <MapPin size={10} className="shrink-0" />
                      <span className="truncate">{d.customerAddress}</span>
                    </div>
                  )}
                  {d.materialDesc && (
                    <div className="text-xs text-slate-400 col-span-2 truncate">{d.materialDesc}</div>
                  )}
                </div>
              </>
            )}

            {/* Asset */}
            {result.type === 'asset' && (
              <>
                <p className="text-sm font-bold text-white mt-1 truncate">{d.name}</p>
                <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
                  {d.category && (
                    <div className="text-xs text-slate-400 truncate">{d.category}</div>
                  )}
                  {d.warehouseName && (
                    <div className="flex items-center gap-1 text-xs text-slate-400">
                      <MapPin size={10} />
                      <span className="truncate">{d.warehouseName}{d.zoneName ? ` · ${d.zoneName}` : ''}</span>
                    </div>
                  )}
                  {d.serialNumber && (
                    <div className="text-xs text-slate-400 col-span-2 truncate">S/N: {d.serialNumber}</div>
                  )}
                </div>
              </>
            )}

            {/* Repair */}
            {result.type === 'repair' && (
              <>
                <p className="text-sm font-bold text-white mt-1 truncate">{d.name}</p>
                <p className="text-xs text-slate-400 mt-0.5 truncate">
                  {d.repair?.description || 'Repair ticket'}
                </p>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  {d.repair?.priority && (
                    <span className="text-xs text-slate-400">Priority: {d.repair.priority}</span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      {result.actions?.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 px-1">
            Quick Actions
          </p>
          {result.actions.map((action) => (
            <ActionButton
              key={action.id}
              action={action}
              onAction={onAction}
              loading={actionLoading === action.id}
              done={actionDone === action.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Main modal ──────────────────────────────────────────────────────────── */
export default function GlobalScannerModal({ onClose }) {
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const [phase, setPhase] = useState('scanning'); // 'scanning' | 'loading' | 'result' | 'error'
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [scannedCode, setScannedCode] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [actionDone, setActionDone] = useState(null);

  /* ── scan callback ───────────────────────────────────────────────────── */
  const handleScan = useCallback(async (code) => {
    setScannedCode(code);
    setPhase('loading');
    try {
      const data = await api.get(`/api/plx/lookup?code=${encodeURIComponent(code)}`);
      setResult(data);
      setPhase('result');
    } catch (err) {
      console.error('[GlobalScanner] Lookup failed:', err);
      setErrorMsg(err?.data?.message || err?.message || 'Lookup failed. Please try again.');
      setPhase('error');
    }
  }, []);

  /* ── action handler ──────────────────────────────────────────────────── */
  const handleAction = useCallback(async (action) => {
    // Navigation-only actions
    if (action.navigate) {
      onClose();
      navigate(action.navigate, action.navigateState ? { state: action.navigateState } : undefined);
      return;
    }

    // API actions
    if (action.action === 'advance_delivery_status') {
      const { shipmentId, newStatus, needsLocation } = action.payload;

      if (needsLocation) {
        // Request location then call ping endpoint
        setActionLoading(action.id);
        try {
          const coords = await new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
              reject(new Error('Geolocation is not supported on this device'));
              return;
            }
            navigator.geolocation.getCurrentPosition(
              (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
              (err) => reject(new Error(err.message || 'Location access denied')),
              { enableHighAccuracy: true, timeout: 15000 }
            );
          });

          await api.post(`/api/delivery/shipments/${shipmentId}/ping`, {
            trigger_status: newStatus === 'In Transit' ? 'in_transit' : 'delivered',
            lat: coords.lat,
            lng: coords.lng,
            accuracy_meters: coords.accuracy,
            device_type: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
            user_agent: navigator.userAgent,
          });
        } catch (err) {
          console.error('[GlobalScanner] Location/ping failed:', err);
          // Fall through to status update anyway
        }
      }

      // Update shipment status
      try {
        await api.patch(`/api/delivery/shipments/${shipmentId}/status`, { status: newStatus });
        setActionDone(action.id);
        // Refresh result
        setTimeout(() => handleScan(scannedCode), 800);
      } catch (err) {
        console.error('[GlobalScanner] Status update failed:', err);
      } finally {
        setActionLoading(null);
      }
      return;
    }

    if (action.action === 'advance_repair_status') {
      const { repairId, newStatus } = action.payload;
      setActionLoading(action.id);
      try {
        await api.patch(`/api/repairs/${repairId}`, { status: newStatus });
        setActionDone(action.id);
        setTimeout(() => handleScan(scannedCode), 800);
      } catch (err) {
        console.error('[GlobalScanner] Repair status update failed:', err);
      } finally {
        setActionLoading(null);
      }
      return;
    }
  }, [navigate, onClose, scannedCode, handleScan]);

  /* ── render ──────────────────────────────────────────────────────────── */
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center z-[200] p-0 sm:p-4">
      <div
        className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl flex flex-col"
        style={{ maxHeight: '92vh', background: '#0f172a', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/8"
          style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
              <ScanLine size={16} className="text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white leading-none">PLX Scanner</p>
              <p className="text-xs text-slate-500 mt-0.5">
                {phase === 'scanning' && 'Point camera at a PLX tag'}
                {phase === 'loading' && `Looking up tag ${scannedCode}…`}
                {phase === 'result' && `Tag ${scannedCode} · ${result?.type ? TYPE_META[result.type]?.label : 'Not found'}`}
                {phase === 'error' && 'Lookup failed'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors"
          >
            <X size={16} className="text-slate-400" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {phase === 'scanning' && (
            <PLXScanner
              onScan={handleScan}
              onClose={onClose}
              isDark={true}
              embedded={true}
            />
          )}

          {phase === 'loading' && (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <div className="w-12 h-12 rounded-full border-2 border-violet-500 border-t-transparent animate-spin" />
              <p className="text-sm text-slate-400">Looking up PLX tag {scannedCode}…</p>
            </div>
          )}

          {phase === 'result' && (
            <div className="p-5">
              <ResultPanel
                result={result}
                isDark={isDark}
                onAction={handleAction}
                actionLoading={actionLoading}
                actionDone={actionDone}
              />
              <button
                onClick={() => { setPhase('scanning'); setResult(null); setScannedCode(null); setActionDone(null); }}
                className="w-full mt-4 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm text-slate-400 hover:text-slate-200 hover:bg-white/5 transition-colors border border-white/8"
                style={{ borderColor: 'rgba(255,255,255,0.08)' }}
              >
                <ScanLine size={14} />
                Scan another tag
              </button>
            </div>
          )}

          {phase === 'error' && (
            <div className="flex flex-col items-center justify-center py-12 gap-4 px-6 text-center">
              <AlertCircle size={40} className="text-red-400" />
              <p className="text-sm text-slate-200 font-medium">Lookup failed</p>
              <p className="text-xs text-slate-500">{errorMsg}</p>
              <button
                onClick={() => { setPhase('scanning'); setErrorMsg(''); setScannedCode(null); }}
                className="mt-2 px-5 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-xl text-sm transition-colors"
              >
                Try again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
